<template>
  <main class="flex-1 relative overflow-y-auto focus:outline-none" tabindex="0">
    <div class="my-6 max-w-1/6 mx-auto px-4 sm:px-6 lg:px-8">
      <div class="md:flex md:items-center md:justify-between">
        <div class="flex min-w-0">
          <svg-vue class="content-title-icon" icon="font-awesome.th-list-solid"></svg-vue>
          <h1 class="content-title">
            {{ $t('Products') }}
          </h1>
        </div>
        <div class="mt-4 flex md:mt-0 md:ml-4">
          <router-link class="btn btn-app rounded-none rounded-l-md" to="/admin/products/new">
            {{ $t('Create') }}
          </router-link>
          <router-link class="btn btn-app rounded-none rounded-r-md" to="/admin">
            {{ $t('Back') }}
          </router-link>
        </div>
      </div>
    </div>
    <div class="my-6 max-w-1/6 mx-auto px-4 sm:px-6 lg:px-8">
      <div class="content-card-body">
        <loading :status="loading"></loading>
        <div class="p-4 items-center md:flex md:justify-between sm:px-6">
          <input type="text" id="search" v-model.lazy="filters.search" :placeholder="$t('Search')" @change="getProducts" class="form-input rounded-none rounded-l-md" />

          <input-select
            :options="productCategories"
            @change="getProducts"
            class="w-full"
            classes="form-input rounded-none"
            id="sorting"
            required
            option-label="name"
            v-model="filters.category"
          ></input-select>
          <div class="flex relative w-full">
            <input-select
              :options="sortOptions"
              @change="changeSort"
              class="w-full"
              classes="form-input rounded-none"
              id="sorting"
              required
              option-label="value"
              v-model="sort.column"
            ></input-select>
          </div>
          <button @click="changeSort" class="flex text-gray-800 rounded-none rounded-r-md font-bold py-2 border border-gray-300 px-6" type="button">
            <svg-vue class="h-4 w-4 text-gray-700" icon="font-awesome.sort-amount-down-alt-regular" v-show="sort.order === 'asc'"></svg-vue>
            <svg-vue class="h-4 w-4 text-gray-700" icon="font-awesome.sort-amount-up-alt-regular" v-show="sort.order === 'desc'"></svg-vue>
            <span class="ml-2">{{ $t('Sort') }}</span>
          </button>
        </div>
        <div class="border-t border-gray-200">
          <template v-if="productList.length > 0">
            <ul>
              <template v-for="(product, index) in productList">
                <li :class="{ 'border-t border-gray-200': index !== 0 }" :key="index">
                  <router-link
                    :to="`/admin/products/${product.id}/edit`"
                    class="block hover:bg-gray-100 focus:outline-none focus:bg-gray-100 transition duration-150 ease-in-out"
                  >
                    <div class="flex items-center px-4 py-4 sm:px-6">
                      <div class="min-w-0 flex-1 flex items-center">
                        <div class="flex-shrink-0">
                          <img :alt="$t('Avatar')" :src="product.avatar !== 'gravatar' ? product.avatar : product.gravatar" class="h-12 w-12 rounded-md" />
                        </div>
                        <div class="min-w-0 flex-1 px-4 lg:grid lg:grid-cols-2 lg:gap-4">
                          <div>
                            <div class="text-sm leading-5 font-medium text-blue-700 truncate">
                              {{ product.name | capitalize }}
                            </div>
                            <div v-if="product.category" class="mt-2 flex items-center text-sm leading-5 text-white">
                              <router-link :to="`/admin/categories/${product.category.id}/edit`"
                                ><span class="truncate rounded bg-gray-800 px-3 py-1">{{ product.category.name | capitalize }}</span></router-link
                              >
                            </div>
                          </div>
                          <div class="hidden lg:block">
                            <div class="text-sm leading-5 text-gray-900">
                              <strong>{{ product.created_at }}</strong>
                            </div>
                            <div class="mt-2 flex items-center text-sm leading-5 text-gray-900" v-show="product.status">
                              <svg-vue class="flex-shrink-0 mr-1.5 h-4 w-4 text-green-400" icon="font-awesome.check-circle-solid"></svg-vue>
                              {{ $t('product is activated') | capitalize }}
                            </div>
                            <div class="mt-2 flex items-center text-sm leading-5 text-red-500" v-show="!product.status">
                              <svg-vue class="flex-shrink-0 mr-1.5 h-4 w-4 text-red-400" icon="font-awesome.times-circle-solid"></svg-vue>
                              {{ $t('product is disabled') | capitalize }}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div>
                        <svg-vue class="h-5 w-5 text-gray-400" icon="font-awesome.angle-right-regular"></svg-vue>
                      </div>
                    </div>
                  </router-link>
                </li>
              </template>
            </ul>
          </template>
          <template v-else>
            <no-content class="my-10" :search="filters.search" />
          </template>
        </div>

        <nav class="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div class="hidden sm:block">
            <p class="text-sm leading-5 text-gray-700">
              {{ $t('Showing') }}
              <span class="font-medium">{{ pagination.perPage * pagination.currentPage - pagination.perPage + 1 }}</span>
              {{ $t('to') }}
              <span class="font-medium">{{
                pagination.perPage * pagination.currentPage <= pagination.total ? pagination.perPage * pagination.currentPage : pagination.total
              }}</span>
              {{ $t('of') }}
              <span class="font-medium">{{ pagination.total }}</span>
              {{ $t('results') }}
            </p>
          </div>
          <div class="flex-1 flex justify-between sm:justify-end">
            <button
              :class="pagination.currentPage <= 1 ? 'opacity-50 cursor-not-allowed' : ''"
              :disabled="pagination.currentPage <= 1"
              @click="changePage(pagination.currentPage - 1)"
              class="pagination-link"
              type="button"
            >
              {{ $t('Previous') }}
            </button>
            <button
              :class="pagination.currentPage >= pagination.totalPages ? 'opacity-50 cursor-not-allowed' : ''"
              :disabled="pagination.currentPage >= pagination.totalPages"
              @click="changePage(pagination.currentPage + 1)"
              class="ml-3 pagination-link"
              type="button"
            >
              {{ $t('Next') }}
            </button>
          </div>
        </nav>
      </div>
    </div>
  </main>
</template>
<script>
export default {
  name: 'list',
  metaInfo() {
    return {
      title: this.$i18n.t('Products'),
    };
  },

  data() {
    return {
      page: 1,
      sort: {
        order: 'asc',
        column: 'created_at',
      },
      sortOptions: [
        { id: 'name', value: this.$i18n.t('Name') },
        { id: 'created_at', value: this.$i18n.t('Created at') },
        { id: 'updated_at', value: this.$i18n.t('Updated at') },
      ],
      filters: {
        search: null,
        category: null,
        supplier: null,
      },
      perPage: 10,
      productCategories: [],
      productSuppliers: [],
      loading: false,
      pagination: {
        currentPage: 0,
        perPage: 0,
        total: 0,
        totalPages: 0,
      },
      productList: [],
    };
  },
  mounted() {
    this.getProducts();
    this.getProductCategories();
    this.getProductSuppliers();
  },
  methods: {
    changePage(page) {
      if (page > 0 && page <= this.pagination.totalPages && page !== this.page) {
        this.page = page;
        this.getProducts();
      }
    },
    changeSort() {
      if (this.sort.order === 'asc') {
        this.sort.order = 'desc';
      } else if (this.sort.order === 'desc') {
        this.sort.order = 'asc';
      }
      this.getProducts();
    },
    getProductCategories() {
      this.loading = true;
      axios
        .get(route('product.categories'))
        .then((response) => {
          this.productCategories = response.data;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    getProductSuppliers() {
      this.loading = true;
      axios
        .get(route('product.suppliers'))
        .then((response) => {
          this.productSuppliers = response.data;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    getProducts() {
      this.loading = true;
      axios
        .get(route('products.index'), {
          params: {
            page: this.page,
            sort: this.sort,
            perPage: this.perPage,
            ...this.filters,
          },
        })
        .then((response) => {
          this.productList = response.data.items;
          this.pagination = response.data.pagination;
          if (this.pagination.totalPages < this.pagination.currentPage) {
            this.page = this.pagination.totalPages;
            this.getProducts();
          } else {
            this.loading = false;
          }
        })
        .catch(() => {
          this.loading = false;
        });
    },
  },
};
</script>
